import { EmptyState, PageHeader, SectionCard, type BreadcrumbItem } from '@verydia/ui';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * The shared shape for every resource page that has no backend yet.
 *
 * §29 and §67 pull in the same direction here: these pages must look like the real
 * thing structurally — header, breadcrumbs, a card, an empty state that names the
 * milestone — without inventing a single row of data. `whatItWillHold` is what
 * turns "nothing here" into "here is what will be here and why it matters".
 */
export interface PlaceholderPageProps {
  title: string;
  description: string;
  breadcrumbs: readonly BreadcrumbItem[];
  icon: ReactNode;
  emptyTitle: string;
  emptyDescription: string;
  availability: string;
  /** Optional supporting panel explaining the resource model. */
  aside?: ReactNode;
}

export function PlaceholderPage({
  title,
  description,
  breadcrumbs,
  icon,
  emptyTitle,
  emptyDescription,
  availability,
  aside,
}: PlaceholderPageProps) {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={title}
        description={description}
        breadcrumbs={breadcrumbs}
        linkComponent={Link}
      />

      <SectionCard flush>
        <EmptyState
          icon={icon}
          title={emptyTitle}
          description={emptyDescription}
          availability={availability}
        />
      </SectionCard>

      {aside}
    </div>
  );
}
